/**
 * Left-rail navigation: categories with article counts + a recently-updated
 * list. Shared between ArticleList and ArticleDetail so the sidebar stays
 * present as the user reads.
 *
 * Visually distinct surface (light fill + border) so it reads as a
 * navigational chrome rather than another content block.
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";

import {
  ResearchArticleIndexItem,
  ResearchCategory,
} from "../../../models/Research";
import { ArticleHeading } from "./headings";

export const ALL_CATEGORY_ID = "__all__";

interface CategoryRailProps {
  categories: ResearchCategory[];
  selected: string;
  onSelect: (id: string) => void;
  recentlyUpdated: ResearchArticleIndexItem[];
  onSelectArticle: (slug: string) => void;
  loading?: boolean;
  /** Headings extracted from the currently-viewed article's body. Empty
   *  on list pages — the "In this article" section just hides itself. */
  tocHeadings?: ArticleHeading[];
}

// Section header style for "CATEGORIES" / "IN THIS ARTICLE" / "RECENTLY
// UPDATED" — uppercase, heavy, generous letter-spacing. Matches the
// dense-but-prominent look of the sidebar mock.
const SIDEBAR_SECTION_LABEL_SX = {
  display: "block",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "1.2px",
  textTransform: "uppercase" as const,
  color: "rgba(0, 0, 0, 0.7)",
};

/**
 * Handles a TOC click. We intercept the default `<a href="#id">` hash
 * navigation so we can:
 *  - call `scrollIntoView({ behavior: 'smooth' })` explicitly, which
 *    works regardless of which element owns viewport scrolling (the
 *    CSS `html { scroll-behavior: smooth }` rule depends on that and
 *    isn't always reliable);
 *  - honor `prefers-reduced-motion: reduce` by falling back to an
 *    instant jump;
 *  - still update the URL hash so deep-linking / copy-link work.
 */
function handleTocClick(
  e: React.MouseEvent<HTMLAnchorElement>,
  id: string,
): void {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  el.scrollIntoView({
    behavior: reducedMotion ? "auto" : "smooth",
    block: "start",
  });
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", `#${id}`);
  }
}

export default function CategoryRail({
  categories,
  selected,
  onSelect,
  recentlyUpdated,
  onSelectArticle,
  loading,
  tocHeadings,
}: CategoryRailProps) {
  const totalCount = categories.reduce(
    (sum, c) => sum + (c.article_count ?? 0),
    0,
  );

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 240 },
        flexShrink: 0,
        mr: { md: 3 },
        mb: { xs: 3, md: 0 },
        alignSelf: "flex-start",
        // Stick to the viewport on md+ so the rail (categories + TOC +
        // recently-updated) stays in view while reading long articles.
        // The Container above this uses `py: 3` (24px) so `top: 24px`
        // here gives the rail the same breathing room from the viewport
        // edge once the page-static NavBar scrolls off. `maxHeight`
        // caps the rail so an unusually tall set of headings gets its
        // own slim inner scrollbar instead of clipping below the fold.
        //
        // (The article column's sticky header uses `top: 0` instead —
        // it can't afford the 24px gap because body content scrolls
        // past it and would flash through. The rail's column has no
        // such content above the rail, so the 24px gap is fine here.)
        //
        // On xs the layout is a single column with the rail above the
        // content — sticky doesn't apply (and wouldn't be useful since
        // the user has to scroll past the rail to reach the article).
        position: { md: "sticky" },
        top: { md: 24 },
        maxHeight: { md: "calc(100vh - 48px)" },
        overflowY: { md: "auto" },
        // Distinct sidebar surface — separates navigational chrome from the
        // main reading column.
        backgroundColor: "#f7f7f7",
        border: "1px solid #e6e6e6",
        borderRadius: "4px",
        p: 2,
      }}
    >
      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Categories</Typography>
      <Stack sx={{ mt: 1 }}>
        <CategoryRow
          label="All"
          count={totalCount}
          active={selected === ALL_CATEGORY_ID}
          onClick={() => onSelect(ALL_CATEGORY_ID)}
        />
        {!loading &&
          categories.map((c) => (
            <CategoryRow
              key={c.id}
              label={c.name}
              count={c.article_count ?? 0}
              active={selected === c.id}
              onClick={() => onSelect(c.id)}
            />
          ))}
      </Stack>

      {tocHeadings && tocHeadings.length > 0 && (
        <>
          <Typography sx={{ ...SIDEBAR_SECTION_LABEL_SX, mt: 4 }}>
            In this article
          </Typography>
          <Stack sx={{ mt: 1.25 }}>
            {tocHeadings.map((h) => (
              <Box
                key={h.id}
                component="a"
                href={`#${h.id}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  handleTocClick(e, h.id)
                }
                sx={{
                  // Indent by level — h1 flush, h2/h3 progressively
                  // indented. Padding-left so the click target spans
                  // the full row.
                  pl: (h.level - 1) * 1.5,
                  py: 0.65,
                  cursor: "pointer",
                  textDecoration: "none",
                  color: "rgba(0, 0, 0, 0.55)",
                  fontSize: "0.95rem",
                  lineHeight: 1.35,
                  "&:hover": { color: "#CF0A2C" },
                }}
              >
                {h.text}
              </Box>
            ))}
          </Stack>
        </>
      )}

      {recentlyUpdated.length > 0 && (
        <>
          <Typography sx={{ ...SIDEBAR_SECTION_LABEL_SX, mt: 4 }}>
            Recently Updated
          </Typography>
          <Stack sx={{ mt: 1 }}>
            {recentlyUpdated.map((a) => (
              <Box
                key={a.id}
                onClick={() => onSelectArticle(a.slug)}
                sx={{
                  py: 0.5,
                  cursor: "pointer",
                  color: "text.primary",
                  fontSize: "0.85rem",
                  lineHeight: 1.35,
                  "&:hover": { color: "#CF0A2C" },
                }}
              >
                {a.title}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

function CategoryRow({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 1,
        py: 0.75,
        cursor: "pointer",
        borderRadius: "3px",
        backgroundColor: active ? "#CF0A2C" : "transparent",
        color: active ? "#fff" : "text.primary",
        fontWeight: active ? 600 : 400,
        fontSize: "0.9rem",
        "&:hover": {
          backgroundColor: active ? "#CF0A2C" : "rgba(0,0,0,0.06)",
        },
      }}
    >
      <span>{label}</span>
      <span style={{ opacity: 0.8 }}>{count}</span>
    </Box>
  );
}
